// Copyright (c) 2026 Broadcom. All rights reserved.
// Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc. and/or its subsidiaries.
//
// AuthContext — provides auth state and actions throughout the app.
//
// Provides:
//   user            — current session user (null when not logged in)
//   isAuthenticated — boolean
//   authLoading     — true while the initial /me check is in flight
//   authError       — non-null string when user is authenticated but not authorised
//                     (AuthHub login succeeded but user has no recognised CDA group)
//   devConfig       — { localBypass, nodeEnv } from /api/auth/dev-config
//   login           — POST username/password (or bypass in local dev)
//   logout          — destroy session + navigate to /login
//   refreshMe       — re-fetch /api/auth/me (called after OIDC redirect-back)

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { API_ENDPOINTS, authFetch } from '../services/api';

// Idle timeout: log the user out after 30 minutes of no keyboard/mouse/touch activity.
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

// Maximum time to wait for the backend to respond to session-check fetches.
// If /api/auth/me or /api/auth/dev-config does not reply within this window,
// the request is aborted and authLoading resolves to false — the user sees the
// login form rather than being stuck on "Please wait…" indefinitely.
const SESSION_FETCH_TIMEOUT_MS = 10_000; // 10 seconds

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]               = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError]     = useState(null);
  const [devConfig, setDevConfig]     = useState({ localBypass: false, nodeEnv: 'development', portalAuthMode: false, portalUrl: '', testAccounts: [] });
  const idleTimerRef                  = useRef(null);

  const isAuthenticated = !!user;
  const isDevTeam       = user?.devTeamAccess === true;

  const clearAuth = useCallback(() => {
    setUser(null);
    setAuthError(null);
  }, []);

  const fetchMe = useCallback(async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SESSION_FETCH_TIMEOUT_MS);
    try {
      const res = await authFetch(API_ENDPOINTS.ME(), { signal: controller.signal });
      clearTimeout(timer);

      if (res.status === 401) {
        clearAuth();
        return null;
      }

      // 403 means the user authenticated via AuthHub but belongs to no recognised
      // CDA group — surface a message so they know why they can't proceed.
      if (res.status === 403) {
        setUser(null);
        setAuthError(
          'Your account does not have access to the Code Disk/Strip Application. ' +
          'Please contact Broadcom Support to request access.'
        );
        return null;
      }

      if (!res.ok) return null;
      const data = await res.json();
      setAuthError(null);
      return data.user || data;
    } catch {
      // Covers both AbortError (timeout) and network failures.
      // In either case, resolve as unauthenticated so authLoading clears.
      clearTimeout(timer);
      return null;
    }
  }, [clearAuth]);

  const refreshMe = useCallback(async () => {
    const userData = await fetchMe();
    if (userData) setUser(userData);
    return userData;
  }, [fetchMe]);

  const fetchDevConfig = useCallback(async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), SESSION_FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(API_ENDPOINTS.DEV_CONFIG(), { signal: controller.signal });
      clearTimeout(timer);
      if (!res.ok) return;
      const data = await res.json();
      setDevConfig(data);
    } catch {
      // Non-critical — ignore (covers AbortError timeout and network failures).
      clearTimeout(timer);
    }
  }, []);

  // On mount: fetch dev config + check for an existing session
  useEffect(() => {
    let cancelled = false;
    setAuthLoading(true);

    Promise.all([fetchDevConfig(), fetchMe()]).then(([, userData]) => {
      if (cancelled) return;
      if (userData) setUser(userData);
      setAuthLoading(false);
    });

    return () => { cancelled = true; };
  }, [fetchMe, fetchDevConfig]);

  // Re-sync when tab regains focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshMe();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshMe]);

  /**
   * Username/password login — POSTs credentials to POST /api/auth/login.
   * When LOCAL_AUTH_BYPASS=false the server returns 400 directing to SSO.
   * When LOCAL_AUTH_BYPASS=true the server creates a mock session.
   */
  const login = useCallback(async (username, password) => {
    let res;
    try {
      res = await fetch(API_ENDPOINTS.LOGIN_POST(), {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({ username, password }),
        credentials: 'include',
      });
    } catch {
      throw new Error('Network error. Please try again.');
    }

    let data;
    try {
      data = await res.json();
    } catch {
      if (!res.ok) throw new Error(`Login failed with status ${res.status}`);
      throw new Error('Invalid response from server.');
    }

    if (!res.ok) {
      throw new Error(data?.error || 'Login failed.');
    }

    await refreshMe();
  }, [refreshMe]);

  const logout = useCallback(() => {
    // Full page navigation so the browser follows the complete redirect chain:
    //   portal environments:     /api/auth/logout → AuthHub end_session → /login
    //   non-portal environments: /api/auth/logout → LOGOUT_REDIRECT_URL
    // Using fetch + redirect:manual would block the cross-site redirect to AuthHub,
    // preventing the AuthHub SSO session from being cleared on portal environments.
    window.location.href = API_ENDPOINTS.LOGOUT();
  }, []);

  // Idle timeout: log out after IDLE_TIMEOUT_MS of no user activity.
  // Placed after logout declaration to avoid TDZ (Cannot access 'logout' before initialization).
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach((evt) => window.addEventListener(evt, resetTimer, { passive: true }));

    resetTimer();

    return () => {
      clearTimeout(idleTimerRef.current);
      activityEvents.forEach((evt) => window.removeEventListener(evt, resetTimer));
    };
  }, [user, logout]);

  const value = useMemo(() => ({
    user,
    isAuthenticated,
    isDevTeam,
    authLoading,
    authError,
    devConfig,
    login,
    logout,
    refreshMe,
    clearAuth,
  }), [user, isAuthenticated, isDevTeam, authLoading, authError, devConfig, login, logout, refreshMe, clearAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
